// Dashboard Inventory (brief owner 15 Sep 2026, G8 — UJI COBA, lihat BACA-DULU-G8.md).
//
// SETIAP BAGIAN DIPUTUSKAN BACKEND (`GET /inventory/dashboard`): bagian yang
// tidak boleh dilihat akun ini datang sebagai `null` dan tidak dirender sama
// sekali. Layar sengaja TIDAK mengetik ulang daftar kunci izin per bagian —
// kalau diketik ulang, suatu hari layar dan backend berbeda pendapat dan
// muncul kartu yang isinya 403 (aturan #3 CLAUDE.md).
import { Link } from "react-router-dom";
import { Boxes, AlertTriangle, Package, PackagePlus, Wallet } from "lucide-react";
import { api, idr, fmtNum } from "../lib/api";
import { useDataQuery } from "@/hooks/useDataQuery";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal } from "@/lib/tanggal";
import { Kpi } from "./Dashboard";

export const INVENTORY_DASHBOARD_QK = ["dashboard-inventory"];

const LABEL_PO = { menunggu_approval: "PO Menunggu Approval", terbuka: "PO Terbuka", sebagian: "PO Diterima Sebagian" };

function JudulBagian({ judul, tautan, label, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-4">
      <h2 className="font-display text-base md:text-lg font-bold text-slate-900">{judul}</h2>
      {tautan && (
        <Link to={tautan} data-testid={testid} className="text-[11px] md:text-xs text-emerald-700 hover:underline">{label}</Link>
      )}
    </div>
  );
}

function BagianStok({ stok }) {
  const jalan = stok.dalam_perjalanan || {};
  const gudang = (stok.per_gudang || []).slice(0, 8);
  return (
    <section className="mb-8 md:mb-10" data-testid="inv-dash-stok">
      <JudulBagian judul="Posisi Stok" tautan="/inventory/stok" label="Buka Stok & Gudang" testid="inv-dash-stok-buka" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-4">
        <Kpi tid="inv-kpi-total-qty" icon={Boxes} label="Total Qty di Gudang" value={fmtNum(stok.total_qty || 0)}
             sub={`${fmtNum(stok.jumlah_sku_gudang || 0)} baris SKU × gudang`} tint="emerald" />
        <Kpi tid="inv-kpi-nilai" icon={Wallet} label="Nilai Persediaan" value={idr(stok.total_nilai || 0)}
             sub="Nilai lapisan biaya — sama dengan Laporan Stok, bukan saldo COA" tint="sky" delay={0.05} />
        <Kpi tid="inv-kpi-rusak" icon={AlertTriangle} label="Unit Rusak" value={fmtNum(stok.qty_rusak || 0)}
             sub="Masih di gudang, tidak bisa dijual" tint="rose" delay={0.1} />
        <Kpi tid="inv-kpi-perjalanan" icon={Package} label="Dalam Perjalanan" value={fmtNum(jalan.qty || 0)}
             sub={`${fmtNum(jalan.jumlah_transfer || 0)} transfer · ${idr(jalan.nilai || 0)}`} tint="amber" delay={0.15} />
      </div>
      {gudang.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-xs md:text-sm" data-testid="inv-dash-per-gudang">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left font-medium px-3 py-2">Gudang</th>
                <th className="text-right font-medium px-3 py-2">Qty</th>
                <th className="text-right font-medium px-3 py-2">Nilai</th>
              </tr>
            </thead>
            <tbody>
              {gudang.map((g, i) => (
                <tr key={g.warehouse_id || `tanpa-${i}`} className="border-t border-slate-100" data-testid={`inv-dash-gudang-${i}`}>
                  <td className="px-3 py-2 text-slate-900">{g.warehouse_nama || "(tanpa gudang)"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(g.qty || 0)}</td>
                  <td className="px-3 py-2 text-right font-mono">{idr(g.nilai || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {stok.tanpa_gudang_baris > 0 && (
        <p className="text-[11px] text-amber-700 mt-2" data-testid="inv-dash-tanpa-gudang">
          {fmtNum(stok.tanpa_gudang_baris)} baris stok lama belum punya gudang — tidak ditebak masuk gudang mana pun.
        </p>
      )}
    </section>
  );
}

function BagianMenipis({ menipis }) {
  const teratas = menipis.teratas || [];
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm" data-testid="inv-dash-menipis">
      <JudulBagian judul="Stok Menipis" tautan="/inventory/pembelian" label="Buka Pembelian" testid="inv-dash-menipis-buka" />
      <p className="font-display text-2xl font-bold text-slate-900" data-testid="inv-dash-menipis-jumlah">{fmtNum(menipis.jumlah || 0)}</p>
      <p className="text-[11px] text-slate-500 mb-3">produk di bawah atau sama dengan stok minimum</p>
      {teratas.length === 0 ? (
        <p className="text-xs text-slate-400">Tidak ada produk yang menipis</p>
      ) : (
        <ul className="space-y-2">
          {teratas.map((p) => (
            <li key={p.sku} className="flex items-center justify-between gap-3 text-xs" data-testid={`inv-dash-menipis-${p.sku}`}>
              <span className="text-slate-900 truncate">{p.nama || p.sku}</span>
              <span className="font-mono text-slate-600 whitespace-nowrap">
                {fmtNum(p.stock_qty || 0)} / min {fmtNum(p.min_stock || 0)}
                {p.saran_qty_pesan ? ` · pesan ${fmtNum(p.saran_qty_pesan)}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BagianHitung({ judul, tautan, label, testid, baris }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm" data-testid={testid}>
      <JudulBagian judul={judul} tautan={tautan} label={label} testid={`${testid}-buka`} />
      <div className="space-y-2">
        {baris.map(([nama, nilai, key]) => (
          <div key={key} className="flex items-center justify-between text-sm" data-testid={`${testid}-${key}`}>
            <span className="text-slate-600">{nama}</span>
            <span className="font-mono font-semibold text-slate-900">{fmtNum(nilai || 0)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function BagianOpname({ opname }) {
  const t = opname.terakhir;
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm" data-testid="inv-dash-opname">
      <JudulBagian judul="Blind Opname Terakhir" tautan="/inventory/opname-blind" label="Buka Blind Opname" testid="inv-dash-opname-buka" />
      {!t ? (
        <p className="text-xs text-slate-400">Belum ada blind opname</p>
      ) : (
        <div className="text-sm space-y-1">
          <p className="text-slate-900 font-medium">{t.code} · {t.warehouse_nama || "-"}</p>
          <p className="text-xs text-slate-500">{t.tanggal ? formatTanggal(t.tanggal) : "-"}{t.opname_dibuat ? " · sudah dijadikan opname" : ""}</p>
          <p className="text-xs text-slate-700">
            Selisih lebih {fmtNum(t.sku_selisih_plus || 0)} SKU ({idr(t.nilai_selisih_plus || 0)}) ·
            selisih kurang {fmtNum(t.sku_selisih_minus || 0)} SKU ({idr(t.nilai_selisih_minus || 0)})
          </p>
        </div>
      )}
    </section>
  );
}

function IsiDashboard({ data }) {
  const po = data.po;
  const tr = data.transfer;
  const rq = data.request_stok;
  return (
    <>
      {data.stok && <BagianStok stok={data.stok} />}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        {data.stok_menipis && <BagianMenipis menipis={data.stok_menipis} />}
        {po && (
          <BagianHitung judul="Pembelian (PO)" tautan="/inventory/pembelian" label="Buka Pembelian" testid="inv-dash-po"
            baris={Object.keys(LABEL_PO).map((k) => [LABEL_PO[k], po[k], k])} />
        )}
        {tr && (
          <BagianHitung judul="Transfer Antar Gudang" tautan="/inventory/stok" label="Buka Stok & Gudang" testid="inv-dash-transfer"
            baris={[["Disiapkan", tr.disiapkan, "disiapkan"], ["Dikirim (belum diterima)", tr.dikirim, "dikirim"]]} />
        )}
        {rq && (
          <BagianHitung
            judul={rq.lingkup === "milik_sendiri" ? "Request Stok Saya" : "Request Stok"}
            tautan="/request-stok" label="Buka Request Stok" testid="inv-dash-request"
            baris={[["Menunggu", rq.menunggu, "menunggu"], ["Diproses", rq.diproses, "diproses"]]} />
        )}
        {data.opname && <BagianOpname opname={data.opname} />}
      </div>
    </>
  );
}

export default function InventoryDashboard() {
  const { data, loading, error, refetch } = useDataQuery(
    INVENTORY_DASHBOARD_QK,
    () => api.get("/inventory/dashboard").then((r) => r.data),
  );
  return (
    <div className="p-4 md:p-8 max-w-[1400px]" data-testid="inventory-dashboard">
      <header className="mb-6 md:mb-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2 inline-flex items-center gap-1.5">
          <PackagePlus className="w-3.5 h-3.5" /> Inventory
        </p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Dashboard Inventory</h1>
        <p className="mt-2 text-xs md:text-sm text-slate-500">
          Posisi stok, stok menipis, pembelian, transfer, request stok, dan blind opname — hanya bagian yang boleh Anda lihat.
        </p>
      </header>
      {/* Galat (termasuk 403) tampil sebagai pesan + Coba lagi, BUKAN skeleton
          yang berputar selamanya — kelas cacat yang sudah ada di Dashboard lama. */}
      <LoadingState loading={loading} error={error} onRetry={refetch} jumlahKartu={4} data-testid="inv-dash-status">
        {data && <IsiDashboard data={data} />}
      </LoadingState>
    </div>
  );
}
